import { RewardListItem } from './program-rewards-list'
import { Text } from '@/components'
import {
  DeviceLimits,
  ProgramRewardsByCategory,
} from '@/types/programs-new/programs-new'
import { GiftIcon } from 'lucide-react'
import {
  calculateDynamicRewardCategoryTotals,
  getMinMaxRangeString,
  SelectedDeviceCounts,
} from './program-rewards-utils'
import { useMemo, useState, useEffect } from 'react'
import NumberFlow from '@number-flow/react'
import DeviceLimitHelpers from '@/app/pages/multi-program-enrollment/device-selection/DeviceLimitHelpers'
import MessageBox from '@/app/components/MessageBox/MessageBox'
import RewardHelpers from '@/app/pages/multi-program-enrollment/rewards/rewardsUtils'
import AlertMessage from '@/app/components/AlertMessage'

export default function DynamicProgramRewardsListCard({
  rewardsByCategory,
  selectedDeviceCounts,
  rewardDeviceLimits,
  offPeakChargingLabel,
}: {
  rewardsByCategory: Omit<ProgramRewardsByCategory, 'referral'> | null
  selectedDeviceCounts: SelectedDeviceCounts
  rewardDeviceLimits: DeviceLimits | null
  offPeakChargingLabel?: string
}) {
  if (!rewardsByCategory) return null
  const rewardItems = useMemo(
    () => [
      ...calculateDynamicRewardCategoryTotals(
        rewardsByCategory,
        selectedDeviceCounts,
        rewardDeviceLimits,
      ),
    ],
    [rewardsByCategory, selectedDeviceCounts],
  )
  const calculatedTotal = useMemo(() => {
    return (
      calculateDynamicRewardCategoryTotals(
        rewardsByCategory,
        selectedDeviceCounts,
        rewardDeviceLimits,
      ) || []
    ).reduce((sum, r) => sum + r.currentTotal, 0)
  }, [rewardsByCategory, selectedDeviceCounts, rewardDeviceLimits])

  const [total, setTotal] = useState(0)

  const limitsText =
    DeviceLimitHelpers.DeviceLimitsAlertText(rewardDeviceLimits)

  const isExternalReward = RewardHelpers.useExternalPayoutCheck()

  useEffect(() => {
    setTotal(calculatedTotal)
  }, [calculatedTotal])

  const showDynamicSummary =
    rewardsByCategory.application ||
    rewardsByCategory.enrollment ||
    rewardsByCategory.participation ||
    rewardsByCategory.endOfProgram
  return (
    <div
      className={`w-full flex flex-col ${
        showDynamicSummary ? 'gap-4' : 'gap-2'
      } border rounded-lg border-solid border-themed-grey-100 p-4`}
    >
      {showDynamicSummary ? (
        <DynamicRewardsYearlySummary amount={total} />
      ) : (
        <Text variant="subheader">Program Rewards</Text>
      )}
      <div className="flex flex-col gap-2">
        {rewardItems.map((r) => (
          <RewardListItem
            title={r.label}
            content={`$${r.currentTotal} total`}
          />
        ))}
        {rewardsByCategory.offPeakCharging && (
          <RewardListItem
            title={offPeakChargingLabel || 'Clean Power Hours'}
            content={`$${getMinMaxRangeString(
              rewardsByCategory.offPeakCharging,
            )} per kWh used`}
          />
        )}
        {limitsText && (
          <div>
            <MessageBox>Rewards only apply for {limitsText}</MessageBox>
          </div>
        )}
        {isExternalReward && (
          <div>
            <AlertMessage variant="info">
              Rewards will be paid outside of Optiwatt by program partner
            </AlertMessage>
          </div>
        )}
      </div>
    </div>
  )
}

function DynamicRewardsYearlySummary({ amount }: { amount: number }) {
  return (
    <div className="flex flex-col bg-base-green-100 items-center justify-center p-4 rounded-lg mt-1">
      <div className="absolute flex items-center justify- center bg-white p-1 border border-solid border-base-grey-100 rounded-full -translate-y-10">
        <GiftIcon className="text-base-green-900 size-6" />
      </div>
      <Text variant="h2" className="text-base-green-900 mt-2 eased-value">
        <NumberFlow
          value={amount}
          prefix={'$'}
          spinTiming={{ duration: 1_000 }}
        />
      </Text>
      <Text variant="body2">total program rewards</Text>
    </div>
  )
}
